import type {
  AppQueryBuilderCompileRequest,
  AppQueryBuilderConceptRef,
} from "@/features/app-studio/api/appQueryBuilderApi";
import {
  resolveSourceTable,
  selectSemanticMeasuresFromGeneratedModel,
} from "@/features/data-hub/model/semanticMeasures";
import { selectSemanticMetricsFromGeneratedModel } from "@/features/data-hub/model/semanticMetrics";
import { selectSemanticDimensionsFromGeneratedModel } from "@/features/data-hub/model/semanticDimensions";

/** "none" grain option: filter by the date range only, without bucketing results by time. */
export const QUERY_BUILDER_NO_GRAIN = "";

/** Fixed grain ladder offered when the model has at least one usable time column. */
const QUERY_BUILDER_TIME_GRAINS = ["day", "week", "month", "quarter"] as const;

export const TEMPORAL_DATA_TYPES = new Set([
  "date",
  "datetime",
  "timestamp",
  "timestamptz",
  "time",
]);
export const TEMPORAL_NAME_PATTERN =
  /(^|_)(date|day|week|month|year|ts|timestamp|datetime)($|_)|_at$/i;

export type QueryBuilderMetricOption = {
  /** Display label. */
  name: string;
  /** Stable identity used as the option value (the concept's `key`, or `name` when it has none). */
  value: string;
  /** Whether `value` is the concept's `key` or its `name`. */
  by: "key" | "name";
  description?: string;
  kind: "measure" | "metric";
};

export type QueryBuilderDimensionOption = {
  /** Display label. */
  name: string;
  /** Stable identity used as the option value (the concept's `key`, or `name` when it has none). */
  value: string;
  /** Whether `value` is the concept's `key` or its `name`. */
  by: "key" | "name";
  description?: string;
};

export type QueryBuilderOptions = {
  metrics: QueryBuilderMetricOption[];
  dimensions: QueryBuilderDimensionOption[];
  supportsTimeRange: boolean;
  timeGrains: string[];
};

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

/** True when any semantic-model entity field looks like a usable date/time column. */
function hasTimeColumn(generatedModel: Record<string, unknown>): boolean {
  const entities = asRecord(generatedModel.document).entities;
  if (!Array.isArray(entities)) {
    return false;
  }
  return entities.some((entity) => {
    const fields = asRecord(entity).fields;
    if (!Array.isArray(fields)) {
      return false;
    }
    return fields.some((field) => {
      const row = asRecord(field);
      const dataType = typeof row.data_type === "string" ? row.data_type.toLowerCase() : "";
      const name = typeof row.name === "string" ? row.name : "";
      return TEMPORAL_DATA_TYPES.has(dataType) || TEMPORAL_NAME_PATTERN.test(name);
    });
  });
}

/**
 * Derive the Form-tab options from a domain's generated semantic model. Mirrors the backend's
 * v1 scope: single-table measures + passthrough (single-component) metrics only, no genuine
 * cross-table metrics (those are rejected by the compile endpoint). Reuses the same document
 * selectors the data-hub semantic-model editors use.
 */
export function buildQueryBuilderOptions(
  generatedModel: Record<string, unknown>,
): QueryBuilderOptions {
  const metrics: QueryBuilderMetricOption[] = [];
  const seen = new Set<string>();

  for (const measure of selectSemanticMeasuresFromGeneratedModel(generatedModel)) {
    const name = (measure.name ?? "").trim();
    if (!name || seen.has(name.toLowerCase()) || !resolveSourceTable(measure)) {
      continue;
    }
    seen.add(name.toLowerCase());
    const key = (measure.key ?? "").trim();
    metrics.push({
      name,
      value: key || name,
      by: key ? "key" : "name",
      description: measure.description,
      kind: "measure",
    });
  }

  for (const metric of selectSemanticMetricsFromGeneratedModel(generatedModel)) {
    const name = (metric.name ?? "").trim();
    if (!name || seen.has(name.toLowerCase()) || metric.executable === false) {
      continue;
    }
    const strategy = (metric.combination_strategy ?? "passthrough").trim();
    const componentCount = metric.component_measures?.length ?? 0;
    if (strategy !== "passthrough" && componentCount > 1) {
      continue; // genuine cross-table metric: excluded in v1 (compile would reject it)
    }
    seen.add(name.toLowerCase());
    const key = (metric.key ?? "").trim();
    metrics.push({
      name,
      value: key || name,
      by: key ? "key" : "name",
      description: metric.description,
      kind: "metric",
    });
  }

  const dimensions: QueryBuilderDimensionOption[] = [];
  for (const dimension of selectSemanticDimensionsFromGeneratedModel(generatedModel)) {
    const name = (dimension.name ?? "").trim();
    const hasColumn =
      Boolean(dimension.expression?.trim()) ||
      Object.keys(asRecord(dimension.source_ref)).length > 0;
    if (name && hasColumn) {
      const key = (dimension.key ?? "").trim();
      dimensions.push({
        name,
        value: key || name,
        by: key ? "key" : "name",
        description: dimension.description,
      });
    }
  }

  const supportsTimeRange = hasTimeColumn(generatedModel);
  return {
    metrics,
    dimensions,
    supportsTimeRange,
    timeGrains: supportsTimeRange ? [...QUERY_BUILDER_TIME_GRAINS] : [],
  };
}

export type QueryBuilderFormState = {
  selectedMetrics: string[];
  selectedDimensions: string[];
  timeRangeEnabled: boolean;
  startDate: string; // ISO yyyy-mm-dd
  endDate: string; // ISO yyyy-mm-dd
  grain: string; // "" = no time bucketing
};

export function createEmptyQueryBuilderFormState(): QueryBuilderFormState {
  return {
    selectedMetrics: [],
    selectedDimensions: [],
    timeRangeEnabled: false,
    startDate: "",
    endDate: "",
    grain: QUERY_BUILDER_NO_GRAIN,
  };
}

/** True when the current selection is complete enough to compile a query. */
export function canCompileQuery(state: QueryBuilderFormState): boolean {
  if (state.selectedMetrics.length === 0) {
    return false;
  }
  if (state.timeRangeEnabled && (!state.startDate || !state.endDate)) {
    return false;
  }
  return true;
}

/** Map selected option values back to their tagged concept refs, dropping unknown values. */
function toConceptRefs(
  selectedValues: string[],
  options: { name: string; value: string; by: "key" | "name" }[],
): AppQueryBuilderConceptRef[] {
  const optionByValue = new Map(options.map((option) => [option.value, option]));
  const refs: AppQueryBuilderConceptRef[] = [];
  for (const value of selectedValues) {
    const option = optionByValue.get(value);
    if (option) {
      refs.push({ by: option.by, value: option.value });
    }
  }
  return refs;
}

export function buildCompileRequest(
  state: QueryBuilderFormState,
  options: QueryBuilderOptions,
): AppQueryBuilderCompileRequest {
  const request: AppQueryBuilderCompileRequest = {
    metrics: toConceptRefs(state.selectedMetrics, options.metrics),
  };
  const dimensionRefs = toConceptRefs(state.selectedDimensions, options.dimensions);
  if (dimensionRefs.length > 0) {
    request.dimensions = dimensionRefs;
  }
  if (state.timeRangeEnabled && state.startDate && state.endDate) {
    request.time_range = {
      start_date: state.startDate,
      end_date: state.endDate,
      grain: state.grain || null,
    };
  }
  return request;
}

/**
 * Rebuild form state from a saved compile-request config, keeping only concept refs that still
 * match a current option (same `by` + `value`). Returns empty state when there is no config.
 */
export function hydrateQueryBuilderFormState(
  config: AppQueryBuilderCompileRequest | null | undefined,
  options: QueryBuilderOptions,
): QueryBuilderFormState {
  if (config === null || config === undefined) {
    return createEmptyQueryBuilderFormState();
  }

  const selectValues = (
    refs: AppQueryBuilderConceptRef[] | undefined,
    available: { value: string; by: "key" | "name" }[],
  ): string[] => {
    const availableKeys = new Set(available.map((option) => `${option.by}:${option.value}`));
    const values: string[] = [];
    for (const ref of refs ?? []) {
      if (availableKeys.has(`${ref.by}:${ref.value}`)) {
        values.push(ref.value);
      }
    }
    return values;
  };

  const selectedMetrics = selectValues(config.metrics, options.metrics);
  const selectedDimensions = selectValues(config.dimensions, options.dimensions);

  if (options.supportsTimeRange && config.time_range) {
    return {
      selectedMetrics,
      selectedDimensions,
      timeRangeEnabled: true,
      startDate: config.time_range.start_date,
      endDate: config.time_range.end_date,
      grain: config.time_range.grain ?? QUERY_BUILDER_NO_GRAIN,
    };
  }

  return {
    selectedMetrics,
    selectedDimensions,
    timeRangeEnabled: false,
    startDate: "",
    endDate: "",
    grain: QUERY_BUILDER_NO_GRAIN,
  };
}
